import { OrigensCheckoutPage } from '@pages/OrigensCheckoutPage'
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router'
import { AuthGate } from './AuthGate'
import { LoginPage } from '@pages/LoginPage'
import { DashboardPage } from '@pages/DashboardPage'
import { VendasPage } from '@pages/VendasPage'
import { FinancasPage } from '@pages/FinancasPage'
import { AssinaturasPage } from '@pages/AssinaturasPage'
import { ReconciliacaoPage } from '@pages/ReconciliacaoPage'
import { AlertasPage } from '@pages/AlertasPage'
import { EquipePage } from '@pages/EquipePage'
import { SaquesPage } from '@pages/SaquesPage'
import { FinancialCachePage } from '@pages/FinancialCachePage'
import { ConfigPage } from '@pages/ConfigPage'
import { CheckoutExperiencePage } from '@pages/CheckoutExperiencePage'
import { AcaoNecessariaPage } from '@pages/AcaoNecessariaPage'
import { WebhooksPage } from '@pages/WebhooksPage'
import { CrmIntegrationPage } from '@pages/CrmIntegrationPage'
import { UsuariosPage } from '@pages/UsuariosPage'
import { UsuarioDetalhePage } from '@pages/UsuarioDetalhePage'
import { ChargebacksPage } from '@pages/ChargebacksPage'
import { AdquirentesPage } from '@pages/AdquirentesPage'
import { ProdutosPage } from '@pages/ProdutosPage'
import { ContabilidadePage } from '@pages/ContabilidadePage'
import { RecuperaPage } from '@pages/RecuperaPage'

const rootRoute = createRootRoute({ component: Outlet })

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: AuthGate,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardPage,
})

const vendasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/vendas',
  component: VendasPage,
})

const financasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/financas',
  validateSearch: (search: Record<string, unknown>): { userId?: string } =>
    typeof search.userId === 'string' && search.userId.trim()
      ? { userId: search.userId.trim() }
      : {},
  component: FinancasPage,
})

const assinaturasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/assinaturas',
  component: AssinaturasPage,
})

const origensCheckoutRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/origens-checkout',
  component: OrigensCheckoutPage,
})

const produtosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/produtos',
  component: ProdutosPage,
})

const reconciliacaoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reconciliacao',
  component: ReconciliacaoPage,
})

const contabilidadeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/contabilidade',
  component: ContabilidadePage,
})

const alertasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/alertas',
  component: AlertasPage,
})

const chargebacksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/chargebacks',
  component: ChargebacksPage,
})

const saquesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/saques',
  component: SaquesPage,
})

const equipeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/equipe',
  component: EquipePage,
})

const usuariosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/usuarios',
  component: UsuariosPage,
})

const usuarioDetalheRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/usuarios/$userId',
  component: UsuarioDetalhePage,
})

const adquirentesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/adquirentes',
  component: AdquirentesPage,
})

const configRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config',
  component: ConfigPage,
})

const financialCacheRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config/cache-financeiro',
  component: FinancialCachePage,
})

const configCheckoutRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config/checkout',
  component: CheckoutExperiencePage,
})

const configCrmRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config/crm',
  component: CrmIntegrationPage,
})

const configWebhooksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config/webhooks',
  component: WebhooksPage,
})

const acaoNecessariaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/acao-necessaria',
  component: AcaoNecessariaPage,
})

const onboardingRedirectRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/onboarding',
  component: () => <Navigate to="/acao-necessaria" replace />,
})

const recuperaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/recupera',
  component: RecuperaPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    indexRoute,
    vendasRoute,
    recuperaRoute,
    financasRoute,
    assinaturasRoute,
    produtosRoute,
    origensCheckoutRoute,
    alertasRoute,
    chargebacksRoute,
    reconciliacaoRoute,
    contabilidadeRoute,
    saquesRoute,
    equipeRoute,
    usuariosRoute,
    usuarioDetalheRoute,
    adquirentesRoute,
    configRoute,
    configWebhooksRoute,
    configCrmRoute,
    configCheckoutRoute,
    financialCacheRoute,
    acaoNecessariaRoute,
    onboardingRedirectRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
